import { useEffect, useState } from "react";
import { resolveAuditReferences } from "@/components/auditLog/auditReferenceResolver";
import { auditReferenceKey } from "@/components/auditLog/auditReference";
import type {
  AuditField,
  AuditReferenceLabels,
} from "@/components/auditLog/auditLogTypes";

export function useAuditReferences(fields: AuditField[]) {
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{
    fields: AuditField[];
    labels: AuditReferenceLabels;
    loading: boolean;
  }>({ fields, labels: {}, loading: false });
  const hasReferences = fields.some((field) => field.reference);

  useEffect(() => {
    const controller = new AbortController();
    setResult({ fields, labels: {}, loading: hasReferences });
    if (!hasReferences) return;
    void resolveAuditReferences(fields, controller.signal).then((labels) => {
      if (!controller.signal.aborted)
        setResult({ fields, labels, loading: false });
    });
    return () => controller.abort();
  }, [fields, hasReferences, retry]);

  const current = result.fields === fields;
  const labels = current ? result.labels : {};
  return {
    labels,
    loading: hasReferences && (!current || result.loading),
    hasReferences,
    hasUnresolved: fields.some(
      ({ reference }) =>
        reference &&
        [reference.before, reference.after].some(
          (id) => id && !labels[auditReferenceKey(reference.model, id)]
        )
    ),
    retry: () => setRetry((value) => value + 1),
  };
}
